import {
  fetchAppTypes,
  fetchDecisions,
  fetchSuggest,
  type DecisionFeatureCollection,
  type DecisionProps,
  type DecisionQuery,
  type UnmappableDecision,
} from "./api.js";
import flatpickr from "flatpickr";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import "flatpickr/dist/flatpickr.min.css";
import {
  ALL_OUTCOMES,
  debounce,
  defaultFilters,
  filtersToSearchParams,
  searchParamsToFilters,
  type FilterState,
  type Outcome,
} from "./filters.js";
import { MapController, PALETTE } from "./map.js";

const DISTRICTS = Array.from({ length: 28 }, (_, i) => `D${String(i + 1).padStart(2, "0")}`);

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

// --- bootstrap from URL -----------------------------------------------------
const today = new Date();
const url = new URL(window.location.href);
let filters: FilterState = searchParamsToFilters(url.searchParams, today);

function parseView(): { center?: [number, number]; zoom?: number } {
  const c = url.searchParams.get("c");
  const z = url.searchParams.get("z");
  const center = c
    ? (c.split(",").map(Number) as number[])
    : null;
  const view: { center?: [number, number]; zoom?: number } = {};
  if (center && center.length === 2 && center.every(Number.isFinite)) {
    view.center = [center[0] as number, center[1] as number];
  }
  if (z && Number.isFinite(Number(z))) view.zoom = Number(z);
  return view;
}
const initialView = parseView();

const map = new MapController(el("map"), initialView.center, initialView.zoom);

// --- legend -----------------------------------------------------------------
function renderLegend(): void {
  const legend = el("legend");
  legend.innerHTML =
    "<h2>Legend</h2>" +
    Object.values(PALETTE)
      .map(
        (s) =>
          `<div class="legend-row"><span class="swatch swatch-${s.shape}" style="background:${s.color}"></span>${s.label}</div>`,
      )
      .join("");
}

// --- outcome toggles --------------------------------------------------------
function renderOutcomes(): void {
  const box = el("outcomes");
  box.innerHTML = ALL_OUTCOMES.map(
    (o) =>
      `<label class="check"><input type="checkbox" data-outcome="${o}" ${
        filters.outcomes.includes(o) ? "checked" : ""
      }> ${PALETTE[o]?.label ?? o}</label>`,
  ).join("");
  box.querySelectorAll<HTMLInputElement>("input[data-outcome]").forEach((input) => {
    input.addEventListener("change", () => {
      const o = input.dataset.outcome as Outcome;
      filters.outcomes = input.checked
        ? [...new Set([...filters.outcomes, o])]
        : filters.outcomes.filter((x) => x !== o);
      onFiltersChanged();
    });
  });
}

// --- development-type (appl_type) multiselect -------------------------------
let allTypes: string[] = [];
function renderTypeList(): void {
  const search = el<HTMLInputElement>("type-search").value.trim().toLowerCase();
  const box = el("types");
  const shown = allTypes.filter((t) => t.toLowerCase().includes(search));
  box.innerHTML = shown
    .map(
      (t) =>
        `<label class="check"><input type="checkbox" data-type="${encodeURIComponent(t)}" ${
          filters.types.includes(t) ? "checked" : ""
        }> ${t}</label>`,
    )
    .join("");
  box.querySelectorAll<HTMLInputElement>("input[data-type]").forEach((input) => {
    input.addEventListener("change", () => {
      const t = decodeURIComponent(input.dataset.type ?? "");
      filters.types = input.checked
        ? [...new Set([...filters.types, t])]
        : filters.types.filter((x) => x !== t);
      onFiltersChanged();
    });
  });
}

// --- district dropdown ------------------------------------------------------
function populateDistrictOptions(): void {
  const select = el<HTMLSelectElement>("district");
  select.innerHTML =
    `<option value="">All districts</option>` +
    DISTRICTS.map((d) => `<option value="${d}" ${filters.district === d ? "selected" : ""}>${d}</option>`).join("");
}
function wireDistrict(): void {
  const select = el<HTMLSelectElement>("district");
  select.addEventListener("change", () => {
    filters.district = select.value;
    onFiltersChanged();
  });
}

// --- date range: calendar picker showing DD/MM/YYYY -------------------------
// flatpickr stores the value as ISO (Y-m-d) for the API, while its altInput
// shows the user a DD/MM/YYYY value and a calendar dropdown. This is locale-
// independent, so the format is guaranteed regardless of the browser.
let fromPicker: FlatpickrInstance;
let toPicker: FlatpickrInstance;

function makePicker(id: string, initial: string, apply: (iso: string) => void): FlatpickrInstance {
  return flatpickr(el<HTMLInputElement>(id), {
    dateFormat: "Y-m-d",
    altInput: true,
    altFormat: "d/m/Y",
    allowInput: true,
    defaultDate: initial,
    onChange: (_dates, dateStr) => {
      apply(dateStr);
      onFiltersChanged();
    },
  }) as FlatpickrInstance;
}

function setupDates(): void {
  fromPicker = makePicker("from", filters.from, (iso) => {
    filters.from = iso;
  });
  toPicker = makePicker("to", filters.to, (iso) => {
    filters.to = iso;
  });
}

function syncDateInputs(): void {
  // Update the pickers without firing onChange (second arg = false).
  fromPicker.setDate(filters.from, false);
  toPicker.setDate(filters.to, false);
}

// --- street autosuggest -----------------------------------------------------
function hideSuggestions(): void {
  const list = el("suggestions");
  list.classList.add("hidden");
  list.innerHTML = "";
}

const runSuggest = debounce((q: string) => {
  fetchSuggest(q)
    .then((suggestions) => {
      const list = el("suggestions");
      if (suggestions.length === 0) {
        hideSuggestions();
        return;
      }
      list.classList.remove("hidden");
      list.innerHTML = suggestions
        .map(
          (s, i) =>
            `<li data-i="${i}" data-lng="${s.lon ?? ""}" data-lat="${s.lat ?? ""}">${s.label}</li>`,
        )
        .join("");
      list.querySelectorAll<HTMLLIElement>("li").forEach((li) => {
        li.addEventListener("click", () => {
          const lng = Number(li.dataset.lng);
          const lat = Number(li.dataset.lat);
          el<HTMLInputElement>("street").value = li.textContent ?? "";
          hideSuggestions();
          if (Number.isFinite(lng) && Number.isFinite(lat)) {
            // Fly to the location; the resulting moveend refetches for that
            // vicinity (bbox), which is how the map "filters" to the street.
            map.flyTo(lng, lat, 16);
          }
        });
      });
    })
    .catch(() => hideSuggestions());
}, 250);

function renderStreetSearch(): void {
  const input = el<HTMLInputElement>("street");
  input.addEventListener("input", () => {
    const q = input.value.trim();
    if (q.length < 2) {
      hideSuggestions();
      return;
    }
    runSuggest(q);
  });
  input.addEventListener("blur", () => setTimeout(hideSuggestions, 150));
}

// --- keyword search (over description / type / address) ---------------------
const runKeywordSearch = debounce(() => {
  syncUrl();
  searchAndFit();
}, 350);

function wireKeyword(): void {
  const input = el<HTMLInputElement>("keyword");
  input.value = filters.keyword;
  input.addEventListener("input", () => {
    filters.keyword = input.value.trim();
    runKeywordSearch();
  });
}

// --- status banners + spinner -----------------------------------------------
function setLoading(on: boolean): void {
  el("spinner").classList.toggle("hidden", !on);
}

function esc(v: unknown): string {
  return String(v ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Details of the current un-mappable decisions, for the click-through modal.
let lastUnmappable: UnmappableDecision[] = [];
let lastUnmappableCount = 0;
// The full current result set, for the "list all shown decisions" modal.
let lastResult: DecisionFeatureCollection | null = null;

function decisionItemHtml(d: DecisionProps): string {
  const outcome = d.decision_type_norm
    ? PALETTE[d.decision_type_norm]?.label ?? d.decision_type_norm
    : "—";
  const rows: Array<[string, unknown]> = [
    ["Address", d.address],
    ["Decision date", d.decision_date],
    ["Outcome", outcome],
    ["Decision type", d.decision_type],
    ["Development type", d.appl_type],
    ["Submission no.", d.submission_no],
    ["Decision no.", d.decision_no],
    ["Description", d.submission_desc],
    ["Lot no.", d.mkts_lotno],
    ["District", d.postal_district],
    ["Postal code", d.postal_code],
  ];
  const body = rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join("");
  return `<div class="umr"><table>${body}</table></div>`;
}

function openUnmappableModal(): void {
  const shown = lastUnmappable.length;
  el("unmappable-title").textContent = `${lastUnmappableCount} decision${
    lastUnmappableCount === 1 ? "" : "s"
  } with no map location`;
  const note =
    shown < lastUnmappableCount ? `<p class="umr-note">Showing the first ${shown}.</p>` : "";
  el("unmappable-list").innerHTML = note + lastUnmappable.map(decisionItemHtml).join("");
  el("unmappable-modal").classList.remove("hidden");
}

function closeUnmappableModal(): void {
  el("unmappable-modal").classList.add("hidden");
}

// --- all-shown-decisions modal ----------------------------------------------
function openDecisionsModal(): void {
  const fc = lastResult;
  if (!fc) return;
  const shown = fc.features.length;
  el("decisions-title").textContent = `${shown} decision${shown === 1 ? "" : "s"} shown`;

  const parts: string[] = [];
  if (fc.truncated) {
    parts.push(`<p class="umr-note">Showing a capped subset — zoom in for the rest.</p>`);
  }
  parts.push(fc.features.map((f) => decisionItemHtml(f.properties)).join(""));

  if (fc.unmappable.length > 0) {
    parts.push(
      `<h3 class="modal-section">No map location (${fc.unmappable_count})</h3>`,
    );
    if (fc.unmappable.length < fc.unmappable_count) {
      parts.push(`<p class="umr-note">Showing the first ${fc.unmappable.length}.</p>`);
    }
    parts.push(fc.unmappable.map(decisionItemHtml).join(""));
  }

  el("decisions-list").innerHTML = parts.join("");
  el("decisions-modal").classList.remove("hidden");
}

function closeDecisionsModal(): void {
  el("decisions-modal").classList.add("hidden");
}

function updateStatus(fc: DecisionFeatureCollection): void {
  const status = el("status");
  const parts: string[] = [];
  const countLabel = `${fc.features.length} decision${fc.features.length === 1 ? "" : "s"} shown`;
  // The count opens the full list modal whenever there's anything to list
  // (shown decisions or matching un-mappable ones); otherwise it's plain text.
  const hasSomethingToList = fc.features.length > 0 || fc.unmappable_count > 0;
  parts.push(
    hasSomethingToList
      ? `<button type="button" class="count count-btn" id="show-decisions">${countLabel}</button>`
      : `<div class="count">${countLabel}</div>`,
  );
  if (fc.features.length === 0) {
    parts.push(`<div class="banner empty">No results in this area for the current filters.</div>`);
  }
  if (fc.unmappable_count > 0) {
    parts.push(
      `<button type="button" class="banner warn linklike" id="show-unmappable">${
        fc.unmappable_count
      } matching decision${
        fc.unmappable_count === 1 ? " has" : "s have"
      } no map location — <strong>view details</strong></button>`,
    );
  }
  if (fc.truncated) {
    parts.push(`<div class="banner warn">Showing a capped subset — zoom in for the rest.</div>`);
  }
  status.innerHTML = parts.join("");
  const btn = document.getElementById("show-unmappable");
  if (btn) btn.addEventListener("click", openUnmappableModal);
  document.getElementById("show-decisions")?.addEventListener("click", openDecisionsModal);
}

// --- fetch orchestration ----------------------------------------------------
function buildQuery(bbox?: [number, number, number, number]): DecisionQuery {
  return {
    bbox,
    from: filters.from || undefined,
    to: filters.to || undefined,
    types: filters.types,
    outcomes: filters.outcomes,
    district: filters.district || undefined,
    keyword: filters.keyword || undefined,
  };
}

function applyResult(fc: DecisionFeatureCollection): void {
  map.setDecisions(fc);
  lastResult = fc;
  lastUnmappable = fc.unmappable;
  lastUnmappableCount = fc.unmappable_count;
  updateStatus(fc);
}

function showError(err: unknown): void {
  el("status").innerHTML = `<div class="banner error">Failed to load decisions: ${
    err instanceof Error ? err.message : String(err)
  }</div>`;
}

let requestSeq = 0;

/** Normal viewport-scoped fetch (on filter change and map move). */
function refetch(): void {
  const seq = ++requestSeq;
  setLoading(true);
  fetchDecisions(buildQuery(map.getBbox()))
    .then((fc) => {
      if (seq !== requestSeq) return; // a newer request superseded this one
      applyResult(fc);
    })
    .catch((err: unknown) => {
      if (seq !== requestSeq) return;
      showError(err);
    })
    .finally(() => {
      if (seq === requestSeq) setLoading(false);
    });
}

/**
 * Keyword-search fetch: query all of Singapore (no bbox) so nothing is missed,
 * then fit the map to the matches. The ensuing moveend triggers a normal
 * viewport refetch which keeps the same set in view.
 */
function searchAndFit(): void {
  const seq = ++requestSeq;
  setLoading(true);
  fetchDecisions(buildQuery(undefined))
    .then((fc) => {
      if (seq !== requestSeq) return;
      applyResult(fc);
      if (fc.features.length > 0) map.fitToFeatures(fc.features);
    })
    .catch((err: unknown) => {
      if (seq !== requestSeq) return;
      showError(err);
    })
    .finally(() => {
      if (seq === requestSeq) setLoading(false);
    });
}

function syncUrl(): void {
  const params = filtersToSearchParams(filters);
  const [lng, lat] = map.getCenter();
  params.set("c", `${lng.toFixed(5)},${lat.toFixed(5)}`);
  params.set("z", map.getZoom().toFixed(2));
  window.history.replaceState(null, "", `?${params.toString()}`);
}

const debouncedRefetch = debounce(refetch, 300);
function onFiltersChanged(): void {
  syncUrl();
  debouncedRefetch();
}

// --- panel toggle (mobile) --------------------------------------------------
const isMobile = (): boolean => window.matchMedia("(max-width: 720px)").matches;

function wirePanelToggle(): void {
  const panel = el("panel");
  const toggle = el("panel-toggle");
  const syncToggleLabel = (): void => {
    toggle.textContent = panel.classList.contains("collapsed") ? "☰ Filters" : "✕ Close";
  };
  // On phones the panel is a drawer over the map — start it closed so the map
  // is visible on load; the ☰ button opens it.
  if (isMobile()) panel.classList.add("collapsed");
  syncToggleLabel();

  toggle.addEventListener("click", () => {
    panel.classList.toggle("collapsed");
    syncToggleLabel();
  });
  // Tapping the map dismisses the open drawer on mobile.
  el("map").addEventListener("click", () => {
    if (isMobile() && !panel.classList.contains("collapsed")) {
      panel.classList.add("collapsed");
      syncToggleLabel();
    }
  });
}

// --- un-mappable details modal ----------------------------------------------
function wireUnmappableModal(): void {
  el("unmappable-close").addEventListener("click", closeUnmappableModal);
  el("unmappable-modal").addEventListener("click", (e) => {
    if (e.target === el("unmappable-modal")) closeUnmappableModal(); // click backdrop
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeUnmappableModal();
  });
}

// --- all-shown-decisions modal ----------------------------------------------
function wireDecisionsModal(): void {
  el("decisions-close").addEventListener("click", closeDecisionsModal);
  el("decisions-print").addEventListener("click", () => window.print());
  el("decisions-modal").addEventListener("click", (e) => {
    if (e.target === el("decisions-modal")) closeDecisionsModal(); // click backdrop
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDecisionsModal();
  });
}

// --- reset ------------------------------------------------------------------
function wireReset(): void {
  el("reset").addEventListener("click", () => {
    filters = defaultFilters(new Date());
    renderOutcomes();
    syncDateInputs();
    populateDistrictOptions();
    el<HTMLInputElement>("type-search").value = "";
    el<HTMLInputElement>("keyword").value = "";
    el<HTMLInputElement>("street").value = "";
    renderTypeList();
    onFiltersChanged();
  });
}

// --- init -------------------------------------------------------------------
function init(): void {
  renderLegend();
  renderOutcomes();
  setupDates();
  populateDistrictOptions();
  wireDistrict();
  renderStreetSearch();
  wireKeyword();
  wirePanelToggle();
  wireReset();
  wireUnmappableModal();
  wireDecisionsModal();
  el<HTMLInputElement>("type-search").addEventListener("input", renderTypeList);

  fetchAppTypes()
    .then((types) => {
      allTypes = types;
      renderTypeList();
    })
    .catch(() => {
      el("types").innerHTML = `<div class="banner error">Could not load development types.</div>`;
    });

  // Refetch on viewport change (debounced) and once the map is ready.
  map.onViewportChange(() => {
    syncUrl();
    debouncedRefetch();
  });
  map.onReady(() => refetch());
}

init();
